package com.qrkontaq.data.dto

import kotlinx.serialization.Serializable

@Serializable
data class RatingDto(
    val average: Double? = null,
    val count: Long = 0,
)

@Serializable
data class DayHoursDto(
    val open: String,
    val close: String,
)

@Serializable
data class PublicRestaurantDto(
    val id: String = "",
    val slug: String,
    val name: String,
    val description: Map<String, String> = emptyMap(),
    val logoUrl: String? = null,
    val coverUrl: String? = null,
    val theme: String = "default",
    val accentColor: String? = null,
    val address: String? = null,
    val lat: Double? = null,
    val lng: Double? = null,
    val phone: String? = null,
    val socials: Map<String, String> = emptyMap(),
    val workingHours: Map<String, DayHoursDto?> = emptyMap(),
    val currency: String = "EUR",
    val languages: List<String> = listOf("uk"),
    val defaultLanguage: String = "uk",
    val menuCategoryThumbs: Boolean = true,
    val menuFeatureFirst: Boolean = true,
    val menuShowPopular: Boolean = true,
    val menuShowChef: Boolean = true,
    val menuShowFilters: Boolean = true,
    val isLive: Boolean = true,
    val rating: RatingDto = RatingDto(),
    val likes: Long = 0,
    val loyaltyEnabled: Boolean = false,
    val loyaltyRatePercent: Int = 5,
    val guestCheckEnabled: Boolean = true,
    val waiterCallEnabled: Boolean = true,
)

@Serializable
data class MenuCategoryDto(
    val id: String,
    val name: Map<String, String>,
    val slug: String,
    val sort: Int = 0,
    val isVisible: Boolean = true,
    val isBar: Boolean = false,
)

@Serializable
data class PublicMenuItemDto(
    val id: String,
    val name: Map<String, String>,
    val description: Map<String, String> = emptyMap(),
    val slug: String,
    val priceMinor: Int,
    val photoUrl: String? = null,
    val weight: String? = null,
    val tags: List<String> = emptyList(),
    val allergens: List<String> = emptyList(),
    val calories: Int? = null,
    val isFeatured: Boolean = false,
    val isAvailable: Boolean = true,
    val likes: Long = 0,
    val rating: RatingDto = RatingDto(),
)

@Serializable
data class PublicMenuCategoryDto(
    val category: MenuCategoryDto,
    val items: List<PublicMenuItemDto>,
)

@Serializable
data class PublicMenuDto(
    val categories: List<PublicMenuCategoryDto>,
)

@Serializable
data class ScanRequestDto(
    val tableLabel: String? = null,
    val categorySlug: String? = null,
    val source: String? = null,
)

@Serializable
data class ScanResponseDto(
    val visitRecorded: Boolean = false,
)

// --- Guest-facing table check (the diner's live receipt) ---

@Serializable
data class PublicOrderItemDto(
    val name: String,
    val priceMinor: Int,
    val qty: Int,
)

@Serializable
data class PublicTableOrderDto(
    val id: String = "",
    val status: String = "", // NEW | COOKING | READY | SERVED
    val items: List<PublicOrderItemDto> = emptyList(),
    val createdAt: String = "",
)

/**
 * Everything a diner needs to render their own check: the still-open orders for
 * their table plus the venue currency. Returned by the public, unauthenticated
 * `GET v1/public/restaurants/{slug}/tables/{label}/orders` endpoint so the QR
 * page can show it live without an admin session.
 */
@Serializable
data class PublicTableCheckDto(
    val tableLabel: String = "",
    val currency: String = "EUR",
    val orders: List<PublicTableOrderDto> = emptyList(),
) {
    val totalMinor: Int
        get() = orders.sumOf { order -> order.items.sumOf { it.priceMinor * it.qty } }

    val isEmpty: Boolean
        get() = orders.none { it.items.isNotEmpty() }
}

/** Picks the best translation: requested language, then uk, then anything. */
fun Map<String, String>.localized(lang: String): String =
    this[lang]?.takeIf { it.isNotBlank() }
        ?: this["uk"]?.takeIf { it.isNotBlank() }
        ?: values.firstOrNull { it.isNotBlank() }
        ?: ""

/** Display symbol for the restaurant's ISO-4217 currency; falls back to the code itself. */
fun currencySymbol(currency: String): String =
    when (currency) {
        "UAH" -> "₴"
        "EUR" -> "€"
        "USD" -> "$"
        "PLN" -> "zł"
        "GBP" -> "£"
        "CZK" -> "Kč"
        "RON" -> "lei"
        "GEL" -> "₾"
        "KZT" -> "₸"
        "TRY" -> "₺"
        else -> currency
    }

fun formatPrice(priceMinor: Int, currency: String): String {
    val whole = priceMinor / 100
    val cents = priceMinor % 100
    val symbol = currencySymbol(currency)
    return if (cents == 0) "$whole $symbol" else "$whole.${cents.toString().padStart(2, '0')} $symbol"
}

/** Minor units → "12.50 ₴": always two decimals (used for loyalty balances). */
fun formatMoneyMinor(minor: Int, currency: String): String {
    val whole = minor / 100
    val cents = minor % 100
    return "$whole.${cents.toString().padStart(2, '0')} ${currencySymbol(currency)}"
}
